/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { buildConversationEndpointUrl } from 'src/core/utils/endpointBuilder/buildConversationEndpointUrl';
import { AzureCsAccountCard } from 'src/luis/appShell/managePane/azureAccountsPage/components/AzureCsAccountCard';
import { Section } from 'src/luis/appShell/managePane/azureAccountsPage/components/PredictionResourcesPivot';
import { AzureResourcesManager } from 'src/modules/azure/components/AzureResourcesManager';
import { AzureResourcesStrategies } from 'src/modules/azure/utils/resourcesStrategies';

const messages = defineMessages({
    buttonLabel: { id: 'publishPane.authoringResourcePivot.buttonLabel', defaultMessage: 'Change authoring resource' },
    changeAccountToastStart: { id: 'portal.authoringResourcePivot.changeAccountToastStart', defaultMessage: 'Changing account ...' },
    changeAccountToastFailed: { id: 'portal.authoringResourcePivot.changeAccountToastFailed', defaultMessage: 'Change account failed' },
    changeAccountToastSuccess: {
        id: 'portal.authoringResourcePivot.changeAccountToastSuccess',
        defaultMessage: 'Change account completed'
    }
});

/**
 * The component displaying the authoring resource
 * inside the azure resources page of a LUIS app
 */
export const AuthoringResourcePivot = observer(() => {
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canListResourceKeys } = mapUserRoleToUserActions(userRole);
    const tracker = useProgressTracker({ onComplete: () => setIsAzureCsAccountSelectModalOpen(false) });

    const [isAzureCsAccountSelectModalOpen, setIsAzureCsAccountSelectModalOpen] = React.useState(false);

    const currentGlobalCsAccount = rootStore.azureStore.session.csAccountStore;

    return (
        <Section tokens={{ childrenGap: 40 }} horizontalAlign="start">
            <Stack.Item>
                <RBACTooltip
                    hidden={canListResourceKeys}
                    displayedUserRole={displayedUserRole}
                    componentSurroundedByToolTip={
                        <PrimaryButton
                            text={localize(messages.buttonLabel)}
                            onClick={() => setIsAzureCsAccountSelectModalOpen(true)}
                            disabled={!canListResourceKeys}
                        />
                    }
                />
            </Stack.Item>

            {currentGlobalCsAccount && (
                <AzureCsAccountCard azureCsAccount={currentGlobalCsAccount} buildEndpointUrl={buildConversationEndpointUrl} />
            )}

            <AzureResourcesManager
                disabled={tracker.isRunning}
                serviceTarget="LUIS.Authoring"
                isSelectResourceVisible={isAzureCsAccountSelectModalOpen}
                onChangeSelectResourceVisible={setIsAzureCsAccountSelectModalOpen}
                strategies={{ resource: resource => AzureResourcesStrategies.luisAuthoringStrategy(resource, currentGlobalCsAccount) }}
                onSelectResource={csAccount =>
                    dispatcher.dispatch('changeAppAuthoringCsAccount', {
                        tracker,
                        csAccount,
                        toastData: {
                            quiet: true,
                            running: { title: localize(messages.changeAccountToastStart) },
                            failed: { title: localize(messages.changeAccountToastFailed) },
                            completed: { title: localize(messages.changeAccountToastSuccess) }
                        }
                    })
                }
            />
        </Section>
    );
});
